'use client'

import { useEffect, useRef } from 'react'

/**
 * Module-level ref to the announcer DOM node.
 * The LiveAnnouncer component sets this on mount;
 * the announce() function writes to it imperatively.
 */
let announcerNode: HTMLDivElement | null = null
let debounceTimer: ReturnType<typeof setTimeout> | null = null
let clearTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Announce a message to screen readers via the aria-live region.
 * Debounced: rapid successive calls (within 150ms) only announce the latest.
 * The message is cleared after 3s so duplicate consecutive messages still trigger.
 */
export function announce(message: string) {
  if (!announcerNode) return

  if (debounceTimer) clearTimeout(debounceTimer)
  if (clearTimer) clearTimeout(clearTimer)

  debounceTimer = setTimeout(() => {
    if (!announcerNode) return
    // Clear first so setting the same message again still triggers a read
    announcerNode.textContent = ''
    // Use rAF to ensure the clear is processed before setting new content
    requestAnimationFrame(() => {
      if (!announcerNode) return
      announcerNode.textContent = message
    })

    // Clear after 3s so the next identical message will trigger again
    clearTimer = setTimeout(() => {
      if (announcerNode) announcerNode.textContent = ''
    }, 3000)
  }, 150)
}

/**
 * Visually-hidden aria-live region. Mount once in the provider tree.
 * Screen readers will announce text changes without any visual output.
 */
export function LiveAnnouncer() {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    announcerNode = ref.current
    return () => {
      announcerNode = null
      if (debounceTimer) clearTimeout(debounceTimer)
      if (clearTimer) clearTimeout(clearTimer)
    }
  }, [])

  return (
    <div
      ref={ref}
      className="sr-only"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    />
  )
}
